import { Injectable, inject } from '@angular/core';
import { Observable, of, switchMap, map, concatMap, throwError, catchError, from, expand, reduce, EMPTY } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { GoodsSkuControllerService } from '@app/api/goods-service/api/goodsSkuController.service';
import { PageInfoGoodsSkuPageVO } from '@app/api/goods-service/model/pageInfoGoodsSkuPageVO';
import { SkuInfo } from '@app/api/goods-service/model/skuInfo';
import {
  PriceBook,
  BasePriceAuditPage,
  PriceItem,
  PriceItemCreate,
  PriceItemPage,
  PriceListApiService,
  PriceRevision,
  FloorMargin,
  SellingPricePolicyItem,
} from '@app/api/price-service';

@Injectable({ providedIn: 'root' })
export class PriceListService {
  private readonly api = inject(PriceListApiService);
  private readonly goodsSku = inject(GoodsSkuControllerService);

  listBooks(): Observable<PriceBook[]> {
    return this.api.listBooks();
  }

  /** Published Active amount for one SKU on a book, used to check market and distribution. */
  publishedAmount(
    bookCode: string,
    priceType: string,
    skuCode: string,
  ): Observable<number | null> {
    const sku = skuCode.trim().toLowerCase();
    return this.listBooks().pipe(
      switchMap((books) => {
        const book = (books ?? []).find((item) => item.code === bookCode);
        if (!book || !sku) {
          return of(null);
        }
        return this.pageItems(book.id, {
          page: 0,
          size: 20,
          skuCode: skuCode.trim(),
          priceType,
          publishedOnly: true,
        }).pipe(
          map((page) => {
            const match = (page?.pageContent ?? []).find(
              (item) =>
                item.status === 'ACTIVE' &&
                item.skuCode?.trim().toLowerCase() === sku &&
                item.amount != null,
            );
            return match?.amount ?? null;
          }),
        );
      }),
    );
  }

  /** Effective SKUs that do not already have this price type on the book. */
  selectableSkus(
    bookId: number,
    priceType: string,
  ): Observable<{ skuCode: string; skuName: string }[]> {
    return this.api.pricedSkuCodes(bookId, priceType).pipe(
      switchMap((priced) => {
        const taken = new Set(
          (priced ?? []).map((code) => code.trim().toLowerCase()),
        );
        return this.effectiveSkus().pipe(
          map((skus) => skus.filter((sku) => !taken.has(sku.skuCode.toLowerCase()))),
        );
      }),
    );
  }

  pageItems(
    bookId: number,
    query: {
      page: number;
      size: number;
      skuCode?: string;
      skuName?: string;
      amountFrom?: number;
      amountTo?: number;
      priceType?: string;
      publishedOnly?: boolean;
      vendorId?: number;
      startDate?: string;
      endDate?: string;
      lifecycleStatus?: string;
    },
  ): Observable<PriceItemPage> {
    return this.api.pageItems(bookId, query).pipe(switchMap((page) => this.attachSkuNames(page)));
  }

  private effectiveSkus(): Observable<{ skuCode: string; skuName: string }[]> {
    return this.skuPage(0).pipe(
      expand((page) =>
        page.hasNextPage ? this.skuPage((page.number ?? 0) + 1) : EMPTY,
      ),
      reduce(
        (rows, page) => rows.concat(page.pageContent ?? []),
        [] as { skuCode?: string; skuName?: string }[],
      ),
      map((rows) =>
        rows
          .filter((row) => row.skuCode?.trim() && row.skuName?.trim())
          .map((row) => ({
            skuCode: row.skuCode!.trim(),
            skuName: row.skuName!.trim(),
          }))
          .sort((left, right) => left.skuName.localeCompare(right.skuName)),
      ),
    );
  }

  private skuPage(page: number): Observable<PageInfoGoodsSkuPageVO> {
    return this.goodsSku
      .getList3(
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        [3],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        page,
        200,
        'skuName',
        true,
      )
      .pipe(switchMap((payload) => this.readSkuPage(payload)));
  }

  // Goods client Accept header is wildcard, so HttpClient may deliver a Blob
  // instead of parsed JSON (same as readSkuInfos / SkuOptionsService).
  private readSkuPage(payload: unknown): Observable<PageInfoGoodsSkuPageVO> {
    const toPage = (raw: unknown): PageInfoGoodsSkuPageVO => {
      if (Array.isArray(raw)) {
        return {
          pageContent: raw,
          number: 0,
          size: raw.length,
          totalElements: raw.length,
          hasNextPage: false,
          empty: raw.length === 0,
          numberOfElements: raw.length,
        };
      }
      return (raw ?? {}) as PageInfoGoodsSkuPageVO;
    };
    if (payload instanceof Blob) {
      return from(payload.text()).pipe(
        map((text) => toPage(text ? JSON.parse(text) : {})),
      );
    }
    return of(toPage(payload));
  }

  private attachSkuNames(page: PriceItemPage): Observable<PriceItemPage> {
    const rows = page?.pageContent ?? [];
    const codes = [
      ...new Set(
        rows
          .filter((item) => item.skuCode && !item.skuName?.trim())
          .map((item) => item.skuCode),
      ),
    ];
    if (codes.length === 0) {
      return of(page);
    }
    return this.goodsSku.getBySkuCodes(codes).pipe(
      switchMap((payload) => this.readSkuInfos(payload)),
      map((infos) => {
        const names = new Map(
          infos
            .filter((info) => info.skuCode && info.skuName?.trim())
            .map((info) => [info.skuCode!.trim().toLowerCase(), info.skuName!.trim()]),
        );
        return {
          ...page,
          pageContent: rows.map((item) => ({
            ...item,
            skuName:
              item.skuName?.trim() ||
              names.get(item.skuCode.trim().toLowerCase()) ||
              undefined,
          })),
        };
      }),
      catchError(() => of(page)),
    );
  }

  /** The generated goods client asks for any type, so the body arrives as a Blob. */
  private readSkuInfos(payload: unknown): Observable<SkuInfo[]> {
    if (payload instanceof Blob) {
      return from(payload.text()).pipe(
        map((text) => (text ? (JSON.parse(text) as SkuInfo[]) : [])),
      );
    }
    return of(Array.isArray(payload) ? payload : []);
  }

  /** Published Active COST rows for one vendor, keyed later by SKU. */
  listVendorCosts(vendorId: number): Observable<PriceItem[]> {
    return this.costItems({ vendorId, size: 200 });
  }

  /** Published Active COST rows for one SKU (all vendors). */
  listSkuCosts(skuCode: string): Observable<PriceItem[]> {
    return this.costItems({ skuCode, size: 50 }).pipe(
      map((items) =>
        items.filter(
          (item) => item.skuCode?.toLowerCase() === skuCode.trim().toLowerCase(),
        ),
      ),
    );
  }

  private costItems(query: {
    skuCode?: string;
    vendorId?: number;
    size: number;
  }): Observable<PriceItem[]> {
    return this.listBooks().pipe(
      switchMap((books) => {
        const book = (books ?? []).find((item) => item.code === 'COST_DEFAULT');
        if (!book) {
          return of([]);
        }
        return this.api
          .pageItems(book.id, {
            page: 0,
            size: query.size,
            skuCode: query.skuCode,
            vendorId: query.vendorId,
            priceType: 'COST',
            publishedOnly: true,
          })
          .pipe(
            map((page) =>
              (page?.pageContent ?? []).filter(
                (item) => item.status === 'ACTIVE',
              ),
            ),
          );
      }),
    );
  }

  createPrice(
    bookId: number,
    body: Omit<PriceItemCreate, 'revisionId'>,
  ): Observable<PriceItem> {
    return this.api.listRevisions(bookId).pipe(
      switchMap((revisions) => this.draftRevision(bookId, revisions)),
      switchMap((revision) =>
        this.api.createItem(bookId, { ...body, revisionId: revision.id }),
      ),
    );
  }

  createBasePrice(
    bookId: number,
    body: Omit<PriceItemCreate, 'revisionId'>,
  ): Observable<PriceItem> {
    return this.api.listRevisions(bookId).pipe(
      switchMap((revisions) => this.draftRevision(bookId, revisions)),
      switchMap((revision) =>
        this.api.createBasePrice(bookId, { ...body, revisionId: revision.id }),
      ),
    );
  }

  updateBasePrice(
    itemId: number,
    amount: number,
    changeReason: string,
  ): Observable<PriceItem> {
    return this.api.updateBasePrice(itemId, amount, changeReason);
  }

  updateEffectiveWindow(
    itemId: number,
    body: {
      startAt?: string | null;
      endAt?: string | null;
      changeReason: string;
    },
  ): Observable<PriceItem> {
    return this.api.updateEffectiveWindow(itemId, body);
  }

  deactivateBasePrice(itemId: number): Observable<PriceItem> {
    return this.api.deactivateItem(itemId);
  }

  deactivateItem(itemId: number): Observable<PriceItem> {
    return this.api.deactivateItem(itemId);
  }

  approveItem(itemId: number): Observable<unknown> {
    return this.api.approveItem(itemId);
  }

  bulkApproveItems(itemIds: number[]) {
    return this.api.bulkApproveItems(itemIds);
  }

  bulkDeactivateItems(itemIds: number[]) {
    return this.api.bulkDeactivateItems(itemIds);
  }

  bulkCancelItems(itemIds: number[]) {
    return this.api.bulkCancelItems(itemIds);
  }

  bulkDeleteItems(itemIds: number[]) {
    return this.api.bulkDeleteItems(itemIds);
  }

  updateItem(itemId: number, body: PriceItemCreate): Observable<PriceItem> {
    return this.api.updateItem(itemId, body);
  }

  deleteItem(itemId: number): Observable<void> {
    return this.api.deleteItem(itemId);
  }

  /**
   * Maker submits the open draft. Approval is a separate person.
   * Returns null when that book has nothing waiting to submit.
   */
  submitLatest(bookId: number): Observable<PriceRevision | null> {
    return this.api.listRevisions(bookId).pipe(
      switchMap((revisions) => {
        const latest = this.latestRevision(revisions);
        if (
          !latest ||
          latest.status === 'PUBLISHED' ||
          latest.status === 'APPROVED' ||
          latest.status === 'SUBMITTED'
        ) {
          return of(latest?.status === 'SUBMITTED' ? latest : null);
        }
        return this.api.submitRevision(latest.id);
      }),
    );
  }

  /**
   * Approve, then publish a revision that somebody else already submitted.
   * Returns null when that book has nothing waiting.
   */
  approveAndPublish(bookId: number): Observable<PriceRevision | null> {
    return this.api.listRevisions(bookId).pipe(
      switchMap((revisions) => {
        const latest = this.latestRevision(revisions);
        if (!latest || latest.status === 'PUBLISHED') {
          return of(null);
        }
        if (latest.status === 'DRAFT' || latest.status === 'REJECTED') {
          return throwError(
            () =>
              new HttpErrorResponse({
                status: 409,
                error: {
                  message: 'Revision must be submitted before approval',
                },
              }),
          );
        }
        return this.advanceRevision(latest);
      }),
    );
  }

  private advanceRevision(revision: PriceRevision): Observable<PriceRevision> {
    const approved =
      revision.status === 'APPROVED' || revision.status === 'PUBLISHED'
        ? of(revision)
        : this.api.approveRevision(revision.id);
    return approved.pipe(
      concatMap((current) =>
        current.status === 'PUBLISHED'
          ? of(current)
          : this.api.publishRevision(current.id),
      ),
    );
  }

  pagePriceChanges(query: {
    page: number;
    size: number;
    skuCode?: string;
    skuName?: string;
    priceBookCode?: string;
    priceType?: string;
    actor?: string;
    startDate?: string;
    endDate?: string;
  }): Observable<BasePriceAuditPage> {
    return this.api.pagePriceChanges(query);
  }

  importPriceItems(file: File) {
    return this.api.importPriceItems(file);
  }

  exportPriceChanges(query: {
    skuCode?: string;
    skuName?: string;
    priceBookCode?: string;
    priceType?: string;
    actor?: string;
    startDate?: string;
    endDate?: string;
    format: 'csv' | 'xlsx';
  }) {
    return this.api.exportPriceChanges(query);
  }

  lookupPriceAsOf(query: {
    skuCode: string;
    atLocal?: string;
    channelCode?: string;
    storeCode?: string;
  }) {
    return this.api.lookupPriceAsOf(query);
  }

  pageBasePriceAudits(query: {
    page: number;
    size: number;
    skuCode?: string;
  }): Observable<BasePriceAuditPage> {
    return this.api.pageBasePriceAudits(query);
  }

  listSellingPrices(): Observable<SellingPricePolicyItem[]> {
    return this.api.listSellingPrices();
  }

  updateSellingPrices(
    items: SellingPricePolicyItem[],
  ): Observable<SellingPricePolicyItem[]> {
    return this.api.updateSellingPrices(items);
  }

  getFloorMargin(): Observable<FloorMargin> {
    return this.api.getFloorMargin();
  }

  updateFloorMargin(
    body: Pick<FloorMargin, 'marginType' | 'marginValue'>,
  ): Observable<FloorMargin> {
    return this.api.updateFloorMargin(body);
  }

  private latestRevision(revisions: PriceRevision[]): PriceRevision | undefined {
    return [...(revisions ?? [])].sort(
      (left, right) => (right.versionNo ?? 0) - (left.versionNo ?? 0),
    )[0];
  }

  private draftRevision(
    bookId: number,
    revisions: PriceRevision[],
  ): Observable<PriceRevision> {
    const editable = revisions.find(
      (revision) =>
        revision.status === 'DRAFT' || revision.status === 'REJECTED',
    );
    return editable
      ? of(editable)
      : this.api.createRevision(bookId, 'OMS price list');
  }
}
